

































import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { mapState, mapGetters } from 'vuex';
import { Optional } from 'frontend/types';
import { debounce } from 'debounce';

import { isMobile } from 'frontend/legacy/utils';
import { track } from 'frontend/utils/analytics';
import { lockScroll } from 'frontend/utils/scroll';
import { postData } from 'frontend/utils/ajax';
import XIcon from 'frontend/components/icons/XIcon.vue';

import { isServerEnv } from 'frontend/utils/environment';

@Component({
  components: {
    XIcon,
  },
  computed: {
    ...mapGetters('banner', ['bannerHeight']),
  },
})
export default class Modal extends Vue {
  $refs!: {
    container: HTMLElement;
    modal: HTMLElement;
  };
  @Prop(String) private readonly axId!: string;
  @Prop({ type: Object, default: () => ({}) }) private readonly axExtra!: object;
  @Prop({ type: Boolean, default: false }) private readonly scrollable!: boolean;
  @Prop({ type: Boolean, default: false }) private readonly darkBackground!: boolean;
  @Prop({ type: Boolean, default: false }) private readonly large!: boolean;
  @Prop({ type: Boolean, default: false }) private readonly small!: boolean;
  @Prop({ type: Boolean, default: false }) private readonly noBorderRadius!: boolean;
  @Prop({ type: Boolean, default: false }) private readonly showCloseButton!: boolean;
  @Prop({ type: Boolean, default: false }) private readonly isReferralModal!: boolean;
  @Prop({ type: Boolean, default: true }) private readonly shouldManageScrollLock!: boolean;
  @Prop({ type: Boolean, default: false }) private readonly stickyPaywallExperimentButton!: boolean;
  @Prop({ type: Boolean, default: false }) private readonly paywall!: boolean | undefined;
  @Prop({ type: Boolean, default: false }) private readonly legal!: boolean | undefined;
  @Prop({ type: String, default: 'grey' }) private readonly closeButtonColor!: string | undefined;
  @Prop({ type: Boolean, default: true, required: false })
  private readonly enableTranslucidCloseButton!: boolean;
  @Prop({ type: Boolean, default: false, required: false })
  private readonly disableCloseButtonOpacity!: boolean;

  private resizeObserver: Optional<ResizeObserver> = undefined;
  private isActuallyScrollable = false;
  private bannerHeight!: number;

  @Watch('stickyPaywallExperimentButton')
  onStickyChange() {
    const modal = this.$refs.container as HTMLElement;
    modal.scrollTop = 0;
  }

  private get isMobile(): boolean {
    return isMobile(this);
  }

  private get modalStyle(): { [key: string]: string } {
    const style: { [key: string]: string } = {};
    if (this.paywall && this.bannerHeight > 0) {
      style.top = `${this.bannerHeight}px`;
    }
    return style;
  }

  private close(): void {
    this.$emit('close');
    document.body.classList.remove('locked');
    this.updateProfile();
    track('ui', {
      action: 'modal',
      element_type: 'modal',
      element_id: 'dismissed_referral_modal',
    });
    const modal = document.getElementById('refer-modal');
    if (modal) {
      modal.style.display = 'none';
    }
  }

  private async updateProfile(): Promise<void> {
    try {
      await postData('/api/v1/profile_card/dismiss-modal/');
    } catch (e) {
      // Ignore
    }
  }

  private onClick(e: MouseEvent) {
    const target = e.target as HTMLElement;
    const container = this.$refs.container;
    if (target === container) {
      this.close();
    }
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (e.key?.toLowerCase() === 'escape') {
      this.close();
    }
  }

  private modalIsOverflowing(): boolean {
    if (!this.$refs.modal) {
      return false;
    }
    return this.$refs.modal.clientHeight > window.innerHeight;
  }

  private get resizeHandler(): () => void {
    return debounce(() => {
      this.isActuallyScrollable = this.scrollable || this.modalIsOverflowing();
    }, 50);
  }

  private initResizeObserver() {
    this.resizeObserver = new ResizeObserver(this.resizeHandler);
    this.resizeObserver.observe(this.$refs.modal);
  }

  private setScrollLock(flag: boolean): void {
    // Some parents mount the Modal in a hidden state so locking would be undesirable
    if (this.shouldManageScrollLock) {
      lockScroll(flag);
    }
  }

  private initAutofocus() {
    // Built in autofocus attr doesn't work reliably. This ensures that it does.
    if (!isServerEnv()) {
      const autofocusElement = this.$el.querySelectorAll('[autofocus]')[0] as Optional<
        HTMLButtonElement | HTMLInputElement
      >;
      if (autofocusElement) {
        switch (autofocusElement.tagName.toLowerCase()) {
          case 'button':
            autofocusElement.focus();
            break;
          case 'input':
            (autofocusElement as HTMLInputElement).focus();
            (autofocusElement as HTMLInputElement).select();
            break;
          default:
          // Noop
        }
      }
    }
  }

  private created() {
    this.isActuallyScrollable = this.scrollable;
  }

  private async mounted() {
    if (this.axId) {
      track('ui', {
        action: 'modal',
        element_type: 'modal',
        element_id: this.axId,
        ...this.axExtra,
      });
    }

    this.setScrollLock(true);

    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('resize', this.resizeHandler);
    this.resizeHandler();
    this.initResizeObserver();

    this.initAutofocus();
  }

  private async destroyed() {
    this.setScrollLock(false);
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('resize', this.resizeHandler);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}
